/**
 * DebugOverlay Component
 *
 * Displays debug information for DetailViewItem2 including variant type,
 * dimensions, zoom level, global ID, and validation warnings.
 *
 * Only renders when global debug mode is enabled.
 *
 * @module DetailViewItem2.DebugOverlay
 */

import React from 'react';
import type { ZoomLevel } from '@/hooks/useZoomPan';
import { AdminAwareIdLink } from '../AdminAwareIdLink';

interface DebugOverlayProps {
  /**
   * Loaded dimensions of the current image
   */
  loadedDimensions: { width: number; height: number } | null;

  /**
   * Current variant type being displayed
   */
  currentVariantType: 'thumbnail' | 'medium' | 'full' | 'unknown';

  /**
   * Current zoom level
   */
  zoomLevel: ZoomLevel;

  /**
   * Global ID of the photo being displayed
   */
  globalId: string;

  /**
   * URL validation result
   */
  urlValidation: {
    urlMismatch: boolean;
    urlClaimsVariant?: string;
    dataIssue?: string;
  };

  /**
   * Dimension validation result (null if not applicable)
   */
  dimensionValidation: {
    valid: boolean;
    warning?: string;
    expectedDimensions?: string;
  } | null;

  /**
   * Focus point (percentage 0-100, where 50/50 is center)
   * Feature 646: Track which part of image is centered in viewport
   */
  focusPoint?: { x: number; y: number };

  /**
   * TransformWrapper scale factor (CSS transform scale)
   * Feature 646: Show CSS transform scaling being applied
   */
  transformScale?: number;
}

/**
 * DebugOverlay - Shows debug information overlay on DetailViewItem2
 *
 * Displays:
 * - Current variant type (thumbnail/medium/full)
 * - Loaded dimensions (width x height)
 * - Current zoom level (fit or percentage)
 * - Global ID (clickable link when admin, plain text otherwise)
 * - Validation warnings (URL mismatches, dimension mismatches)
 * - Data pipeline issues (stringified variants)
 *
 * Color coding:
 * - Green checkmark (✓) = All validations passed
 * - Yellow warning (⚠) = Validation issue detected
 * - Red text = Critical data pipeline issue
 *
 * Related: Issue #294 - Admin visibility consolidation
 *
 * @example
 * ```tsx
 * {globalDebugMode && loadedDimensions && (
 *   <DebugOverlay
 *     loadedDimensions={loadedDimensions}
 *     currentVariantType={currentVariantType}
 *     zoomLevel={zoomLevel}
 *     globalId={photo.globalId}
 *     shouldShowAdmin={shouldShowAdmin}
 *     urlValidation={urlValidation}
 *     dimensionValidation={dimensionValidation}
 *   />
 * )}
 * ```
 */
export const DebugOverlay: React.FC<DebugOverlayProps> = ({
  loadedDimensions,
  currentVariantType,
  zoomLevel,
  globalId,
  urlValidation,
  dimensionValidation,
  focusPoint,
  transformScale,
}) => {
  if (!loadedDimensions) {
    return null;
  }

  const hasWarning = urlValidation.urlMismatch || dimensionValidation?.valid === false;

  return (
    <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded font-mono pointer-events-none z-50">
      <div className="flex items-center gap-1">
        <span className={hasWarning ? 'text-yellow-400' : ''}>
          {hasWarning ? '⚠' : '✓'}
        </span>
        <span className={urlValidation.urlMismatch ? 'text-yellow-400' : ''}>
          {currentVariantType.charAt(0).toUpperCase() + currentVariantType.slice(1)}
        </span>
        <span className="text-gray-300">
          {loadedDimensions.width}x{loadedDimensions.height}
        </span>
      </div>

      <div className="text-gray-400 text-[10px] mt-0.5">
        Zoom: {zoomLevel === 'fit' ? 'Fit' : `${zoomLevel}%`}
      </div>

      {transformScale !== undefined && (
        <div className="text-blue-400 text-[10px] mt-0.5">
          CSS Scale: {transformScale.toFixed(2)}x
        </div>
      )}

      {focusPoint && (
        <div className="text-green-400 text-[10px] mt-0.5">
          Focus: {focusPoint.x.toFixed(1)}, {focusPoint.y.toFixed(1)}
        </div>
      )}

      <div className="text-gray-400 text-[10px] mt-0.5">
        <AdminAwareIdLink id={globalId} />
      </div>

      {urlValidation.dataIssue && (
        <div className="text-red-400 text-[10px] mt-0.5">
          ⚠ {urlValidation.dataIssue}
        </div>
      )}

      {urlValidation.urlMismatch && urlValidation.urlClaimsVariant && (
        <div className="text-yellow-400 text-[10px] mt-0.5">
          URL field: {urlValidation.urlClaimsVariant}Url (misconfigured)
        </div>
      )}

      {dimensionValidation?.valid === false && dimensionValidation.expectedDimensions && (
        <div className="text-yellow-400 text-[10px] mt-0.5">
          Expected: {dimensionValidation.expectedDimensions}
        </div>
      )}
    </div>
  );
};

DebugOverlay.displayName = 'DebugOverlay';
